import type { DocumentType } from '@kmdx/contracts';
import { Check, RotateCcw } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { savePhoto } from '../lib/actions';
import { DOCUMENT_LABELS } from '../lib/labels';
import { isPaper } from '../lib/requirements';
import type { PhotoRecord } from '../lib/types';
import { useSettings } from '../state/settings';
import { CameraCapture } from './camera';
import { Banner, BigButton, Label } from './ui';

export interface CaptureResult {
  photos: PhotoRecord[];
  /** Само при километраж — числото, което шофьорът е прочел от таблото. */
  odometerKm: number | null;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  СНИМАЙ → ВИЖ → ПОТВЪРДИ
 *
 *  Всяка снимка минава през екран „Ясно ли е?", преди да влезе в опашката.
 *  Шофьорът гледа самия кадър — цял ли е листът, чете ли се, личат ли
 *  подписите — и чак тогава го потвърждава. Размазана ЧМР, открита в офиса
 *  след три дни, струва нов курс до клиента; открита тук — пет секунди.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function CaptureFlow({
  type,
  title,
  tripId,
  orderStopId,
  replacesDocumentId = null,
  multiPage,
  askOdometer = false,
  odometerMoment = 'start',
  minOdometerKm = null,
  hint,
  checklist,
  onDone,
  onClose,
}: {
  type: DocumentType;
  title?: string;
  tripId: string | null;
  orderStopId: string | null;
  replacesDocumentId?: string | null;
  /** ЧМР е 3–4 листа: след всеки потвърден — „Още страница" или „Готово". */
  multiPage?: boolean;
  /** Километраж: освен снимката се въвежда и числото. */
  askOdometer?: boolean;
  /** При тръгване или при връщане — отива като бележка към снимката за офиса. */
  odometerMoment?: 'start' | 'end';
  /** Показанието при тръгване — числото при връщане не бива да е по-малко. */
  minOdometerKm?: number | null;
  /** Подсказка в камерата и точки за проверка — когато общите не пасват (касова бележка). */
  hint?: string;
  checklist?: string[];
  onDone: (result: CaptureResult) => void;
  onClose: () => void;
}) {
  const quality = useSettings((state) => state.photoQuality);
  const pages = multiPage ?? isPaper(type);
  const heading = title ?? DOCUMENT_LABELS[type];

  const [stage, setStage] = useState<'camera' | 'confirm' | 'more'>('camera');
  const [shot, setShot] = useState<{ blob: Blob; url: string } | null>(null);
  const [saved, setSaved] = useState<PhotoRecord[]>([]);
  const [km, setKm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Временният адрес на кадъра се освобождава, щом кадърът се смени или екранът се затвори. */
  useEffect(() => {
    if (!shot) return;
    return () => URL.revokeObjectURL(shot.url);
  }, [shot]);

  const odometer = askOdometer ? parseKm(km) : null;
  const belowStart = odometer !== null && minOdometerKm !== null && odometer < minOdometerKm;

  async function confirm() {
    if (!shot) return;
    if (askOdometer && odometer === null) {
      setError('Напишете километрите от таблото — само цифрите.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const photo = await savePhoto({
        file: shot.blob,
        tripId,
        orderStopId,
        type,
        quality,
        confirmedAt: new Date().toISOString(),
        replacesDocumentId,
        note:
          askOdometer && odometer !== null
            ? `Километраж при ${odometerMoment === 'end' ? 'връщане' : 'тръгване'}: ${odometer.toLocaleString('bg-BG')} км`
            : null,
      });
      const all = [...saved, photo];
      setSaved(all);
      setShot(null);
      if (pages && !askOdometer) setStage('more');
      else onDone({ photos: all, odometerKm: odometer });
    } catch (issue) {
      setError(issue instanceof Error ? issue.message : 'Снимката не можа да се запише');
    } finally {
      setBusy(false);
    }
  }

  if (stage === 'camera') {
    return (
      <CameraCapture
        title={saved.length > 0 ? `${heading} · стр. ${saved.length + 1}` : heading}
        hint={hint ?? hintFor(type, saved.length)}
        onCapture={(blob) => {
          setShot({ blob, url: URL.createObjectURL(blob) });
          setError(null);
          setStage('confirm');
        }}
        onClose={() => (saved.length > 0 ? setStage('more') : onClose())}
      />
    );
  }

  if (stage === 'more') {
    return (
      <Overlay>
        <div className="space-y-4 rounded-2xl bg-paper p-4">
          <div className="flex items-center gap-3">
            <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-go text-white">
              <Check size={28} strokeWidth={3} />
            </span>
            <div>
              <div className="text-2xl font-bold">{heading}</div>
              <div className="text-lg text-muted">
                Потвърдени: {saved.length} {saved.length === 1 ? 'страница' : 'страници'}
              </div>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {saved.map((photo) => (
              <img key={photo.id} src={photo.thumbDataUrl} alt={`стр. ${photo.page}`} className="h-24 w-20 shrink-0 rounded-lg object-cover" />
            ))}
          </div>
          <BigButton tone="plain" onClick={() => setStage('camera')}>
            Още страница
          </BigButton>
          <BigButton tone="accent" icon={<Check size={28} strokeWidth={3} />} onClick={() => onDone({ photos: saved, odometerKm: null })}>
            Готово — това са всички
          </BigButton>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay>
      <div className="flex max-h-full flex-col gap-2.5 overflow-y-auto rounded-2xl bg-paper p-3">
        <div>
          <div className="text-2xl font-bold">Ясно ли е?</div>
          <div className="text-lg text-muted">
            {heading}
            {pages && !askOdometer ? ` · стр. ${saved.length + 1}` : ''}
          </div>
        </div>
        {shot ? (
          <img src={shot.url} alt={heading} className="max-h-[34svh] w-full rounded-xl bg-black object-contain" />
        ) : null}
        <ul className="space-y-0.5 text-base leading-snug">
          {(checklist ?? checklistFor(type)).map((line) => (
            <li key={line} className="flex gap-2">
              <Check size={20} className="mt-0.5 shrink-0 text-go" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
        {askOdometer ? (
          <label className="block">
            <Label>Километри на таблото</Label>
            <input
              value={km}
              onChange={(event) => setKm(event.target.value)}
              inputMode="numeric"
              autoFocus
              placeholder="например 184 532"
              className="mt-1 w-full rounded-xl border-2 border-line bg-paper px-4 py-4 text-3xl font-bold tabular-nums"
            />
          </label>
        ) : null}
        {belowStart ? (
          <Banner tone="warn">
            По-малко е от километража при тръгване ({minOdometerKm?.toLocaleString('bg-BG')} км). Проверете числото —
            ако е вярно, потвърдете и офисът ще го провери по снимките.
          </Banner>
        ) : null}
        {error ? <Banner tone="stop">{error}</Banner> : null}
        {/* Една под друга: две големи думи една до друга не се побират на 375 px. */}
        <div className="space-y-2">
          <BigButton
            tone="accent"
            disabled={busy || (askOdometer && odometer === null)}
            icon={<Check size={28} strokeWidth={3} />}
            onClick={() => void confirm()}
          >
            Ясно е
          </BigButton>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setShot(null);
              setStage('camera');
            }}
            className="tap flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-line bg-card px-4 py-3 text-xl font-bold disabled:opacity-40"
          >
            <RotateCcw size={22} />
            Снимай пак
          </button>
        </div>
      </div>
    </Overlay>
  );
}

function Overlay({ children }: { children: ReactNode }) {
  return (
    <div className="safe-top safe-bottom fixed inset-0 z-50 flex flex-col justify-center bg-black/85 p-3">{children}</div>
  );
}

/** Само цифрите — „184 532", „184.532" и „184532 км" са едно и също число. */
function parseKm(raw: string): number | null {
  const digits = raw.replace(/[^\d]/g, '');
  if (digits.length === 0 || digits.length > 7) return null;
  const value = Number(digits);
  return Number.isInteger(value) && value > 0 && value <= 3_000_000 ? value : null;
}

function hintFor(type: DocumentType, taken: number): string {
  if (type === 'odometer') return 'Снимай таблото така, че километрите да се четат.';
  if (type === 'photo_loading' || type === 'photo_damage' || type === 'photo_seal') return 'Хвани товара отблизо и на светло.';
  if (taken > 0) return 'Следващият лист — цял, в рамката.';
  return type === 'cmr'
    ? 'Сложи целия лист в рамката. ЧМР има няколко листа — снимай ги един по един.'
    : 'Сложи целия лист в рамката.';
}

function checklistFor(type: DocumentType): string[] {
  if (type === 'odometer') return ['Километрите се четат ясно, без отблясък'];
  if (type === 'photo_loading' || type === 'photo_damage' || type === 'photo_seal') {
    return ['Снимката е ясна, не е размазана', 'Вижда се какво снимате'];
  }
  return [
    'Целият лист е в кадъра — и четирите ъгъла',
    'Текстът се чете, снимката не е размазана',
    'Подписите и печатите се виждат',
  ];
}
